import { MoreHorizontal } from '@creditornot/cb-icons';
import { cbSemanticCssVars } from '@creditornot/cb-ingredients';
import { MenuActionItem, MenuActionItemVariant, MenuItemGroup } from '@creditornot/cb-menu';
import { LegacyPopover } from '@creditornot/cb-popover';
import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { CampaignType } from 'apis/restaurant-api/merchant-admin/campaigns/types';
import { Typography } from 'common/typography';

import ConfirmDeleteModal from './modals/ConfirmDeleteModal';
import ConfirmTerminateModal from './modals/ConfirmTerminateModal';
import { CampaignAction } from '../types';

type CampaignActionsPopoverProps = {
  campaignId: string;
  campaignType: CampaignType;
  allowedActions: CampaignAction[];
  onEditCampaign: (campaignId: string, campaignType: CampaignType) => void;
  onCloneCampaign: (campaignId: string) => void;
  onDeleteCampaign: (campaignId: string) => void;
  onTerminateCampaign: (campaignId: string) => void;
  onStopCampaign: (campaignId: string) => void;
  onRestartCampaign: (campaignId: string) => void;
};

type ActionItem = { onClick: () => void; text: string; variant: MenuActionItemVariant };

const StyledMoreHorizontal = styled(MoreHorizontal)`
  fill: ${cbSemanticCssVars.textSecondary};
  width: 16px;
  height: 16px;
  margin: 8px 16px;
`;

const StyledPopover = styled(LegacyPopover)`
  z-index: 10;
`;

const ActionsPopoverContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 4px;
  gap: 8px;
  width: 172px;
`;

const CampaignActionsPopover = ({
  campaignId,
  campaignType,
  allowedActions,
  onEditCampaign,
  onCloneCampaign,
  onDeleteCampaign,
  onTerminateCampaign,
  onStopCampaign,
  onRestartCampaign,
}: CampaignActionsPopoverProps) => {
  const [isConfirmTerminateModalOpen, setIsConfirmTerminateModalOpen] = useState<boolean>(false);
  const [isConfirmDeleteModalOpen, setIsConfirmDeleteModalOpen] = useState<boolean>(false);
  const { formatMessage } = useIntl();

  const actionsConfig: Partial<Record<CampaignAction, ActionItem>> = {
    edit: {
      onClick: () => {
        onEditCampaign(campaignId, campaignType);
      },
      text: formatMessage({ id: 'campaigns.edit' }),
      variant: 'black' as MenuActionItemVariant,
    },
    clone: {
      onClick: () => {
        onCloneCampaign(campaignId);
      },
      text: formatMessage({ id: 'campaigns.clone' }),
      variant: 'black' as MenuActionItemVariant,
    },
    terminate: {
      onClick: () => {
        setIsConfirmTerminateModalOpen(true);
      },
      text: formatMessage({ id: 'campaigns.end' }),
      variant: 'red' as MenuActionItemVariant,
    },
    stop: {
      onClick: () => {
        onStopCampaign(campaignId);
      },
      text: formatMessage({ id: 'campaigns.stop' }),
      variant: 'red' as MenuActionItemVariant,
    },
    restart: {
      onClick: () => {
        onRestartCampaign(campaignId);
      },
      text: formatMessage({ id: 'campaigns.restart' }),
      variant: 'black' as MenuActionItemVariant,
    },
    delete: {
      onClick: () => {
        setIsConfirmDeleteModalOpen(true);
      },
      text: formatMessage({ id: 'campaigns.delete' }),
      variant: 'red' as MenuActionItemVariant,
    },
  };

  const actions: ActionItem[] = Object.entries(actionsConfig)
    .filter(([action]) => allowedActions.includes(action as CampaignAction))
    .map(([, action]) => action);

  return (
    <div onClick={evt => evt.stopPropagation()}>
      <ConfirmTerminateModal
        isOpen={isConfirmTerminateModalOpen}
        onTerminate={() => {
          onTerminateCampaign(campaignId);
          setIsConfirmTerminateModalOpen(false);
        }}
        onBack={() => {
          setIsConfirmTerminateModalOpen(false);
        }}
      />
      <ConfirmDeleteModal
        isOpen={isConfirmDeleteModalOpen}
        onDelete={() => {
          onDeleteCampaign(campaignId);
          setIsConfirmDeleteModalOpen(false);
        }}
        onBack={() => {
          setIsConfirmDeleteModalOpen(false);
        }}
      />
      <StyledPopover
        placement="bottom-start"
        showOnClick
        showOnHover={false}
        content={() => (
          <ActionsPopoverContainer>
            <MenuItemGroup>
              {actions.map(action => (
                <MenuActionItem key={action.text} onClick={action.onClick} variant={action.variant}>
                  <Typography.Body3>{action.text}</Typography.Body3>
                </MenuActionItem>
              ))}
            </MenuItemGroup>
          </ActionsPopoverContainer>
        )}
      >
        <StyledMoreHorizontal />
      </StyledPopover>
    </div>
  );
};

export default CampaignActionsPopover;
